import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./holdingsDrawer.module.css";

interface DrawerShellProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly headerLeft?: React.ReactNode;
  readonly headerRight?: React.ReactNode;
  readonly subtitle?: React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly children: React.ReactNode;
}

export default function DrawerShell({
  isOpen,
  onClose,
  title,
  headerLeft,
  headerRight,
  subtitle,
  footer,
  children,
}: DrawerShellProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={`${styles.overlay}${isOpen ? ` ${styles.overlayOpen}` : ""}`}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`${styles.panel}${isOpen ? ` ${styles.panelOpen}` : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.drawerHeader}>
          <div className={styles.headerAction}>{headerLeft}</div>
          <div className={styles.headerContent}>
            <h2 className={styles.drawerTitle}>{title}</h2>
            {subtitle !== undefined && (
              <div className={styles.drawerSubtitle}>{subtitle}</div>
            )}
          </div>
          <div className={styles.headerAction}>{headerRight}</div>
        </div>
        <div className={styles.drawerContent}>{children}</div>
        {footer !== undefined && (
          <div className={styles.drawerFooter}>{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
}
